// summary-ui.ts — the in-game WIN / LOSE RESULTS SUMMARY overlay (domObjectivesSummary), the modal
// every mission-end path raises via env.domObjectivesSummary (EndMission / pause-menu abort / MP).
// It builds an ObjectivesView in the win/lose summary variant: WIN → OK; LOSE → RETRY/ABORT/LOAD
// SAVED; the chosen kidc routes through onChoice (the shared post-modal handler). DISPLAY-only —
// no Message constructed here. Lifted from main.ts's mountSummary, a sibling of objectives-ui.ts.
// The mission editor's Play host (SP) uses it now; main.ts migrates onto it too, so they converge.
//
// The MP-replay affordance (VIEW REPLAY) is main.ts-specific, so it is PARAMETERIZED as optional
// deps (onWatchReplay / replayKey / onMpShow): SP callers (the editor) omit them and the summary is
// OK / RETRY-only. setReplayReady() lights up the VIEW REPLAY button on a LIVE summary after the
// server announces the recording (main.ts's onReplayAvailable) — a no-op when no summary is up.

import { ObjectivesView } from './objectives-view.ts';
import type { ObjectivesSummaryInfo } from '../../ui/objectives.ts';
import {
  extractAtlasArt,
  titleArtToDataUrl,
  extractButtonArt,
  type ButtonArt,
} from '../title-art.ts';
import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';

export interface SummaryUiDeps {
  /** the texture atlas mgr — sources the title-screen backdrop (titlescreenbkgd.png) + the
   *  9-slice button frame (extractButtonArt). */
  tam: TexAtlasMgr;
  /** optional pre-extracted 9-slice button art; else extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
  /** play a GUI sfx on a button press, wired to the host SoundMgr (no-op headless). */
  playSfx: (sfx: number) => void;
  /** the element the overlay root mounts into (default document.body). The root is
   *  `position:fixed; inset:0`; a TRANSFORMED pane-covering host element bounds it to the
   *  pane instead of the window (the mission editor's play-in-place WYSIWYG mode). */
  host?: HTMLElement;
  /** MP: open the standalone replay viewer with the announced recording key. SP ⇒ omit. */
  onWatchReplay?: (key: string) => void;
  /** MP: the server-announced recording key (null until ScReplayAvailable arrives). SP ⇒ omit. */
  replayKey?: () => string | null;
  /** MP: bookkeeping when a fresh MP summary shows (main.ts clears latestReplayKey so a prior
   *  game's recording is never offered). SP ⇒ omit. */
  onMpShow?: () => void;
}

export interface SummaryUi {
  /** the domObjectivesSummary session opt (gameSessionOpts.domObjectivesSummary) — every mission-
   *  end path calls this; the chosen kidc routes through onChoice (the shared post-modal handler). */
  domObjectivesSummary: (info: ObjectivesSummaryInfo, onChoice: (idc: number) => void) => void;
  /** MP: light up VIEW REPLAY on a LIVE summary (main.ts onReplayAvailable, after the server
   *  announces the recording). A no-op when no summary is mounted. */
  setReplayReady(): void;
  /** idempotently unmount the summary if it's up (game-over / host close). */
  teardown(): void;
}

/** Stand up the shared in-game win/lose summary overlay for one play session. */
export function createSummaryUI(deps: SummaryUiDeps): SummaryUi {
  const { tam, playSfx } = deps;
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;
  const objBgArt = extractAtlasArt(tam, 'titlescreenbkgd.png');
  const titleBg = objBgArt !== null ? titleArtToDataUrl(objBgArt) : undefined;

  const root = document.createElement('div');
  root.id = 'dom-objectives-summary';
  root.style.display = 'none';
  (deps.host ?? document.body).appendChild(root);
  let view: ObjectivesView | null = null;

  const unmount = (): void => {
    root.style.display = 'none';
    view?.destroy();
    view = null;
    root.replaceChildren();
  };

  const domObjectivesSummary = (
    info: ObjectivesSummaryInfo,
    onChoice: (idc: number) => void,
  ): void => {
    root.style.display = 'flex';
    // MP summary belongs to a fresh game — clear any prior game's announced key so a stale recording
    // is never offered (the new game's key arrives later via onReplayAvailable → setReplayReady).
    if (info.mp) deps.onMpShow?.();
    view = new ObjectivesView({
      snapshot: info.snapshot,
      root,
      kind: info.kind,
      mp: info.mp,
      missionResult: info.missionResult,
      // MP: offer a VIEW REPLAY affordance — PENDING until the server announces the recording (a
      // player knocked out early in a 3-4p game sees the pending hint become a button once the game
      // is won, if they keep the summary up). onWatchReplay opens the standalone viewer.
      showReplay: info.mp,
      replayReady: info.mp && (deps.replayKey?.() ?? null) !== null,
      onWatchReplay: () => deps.onWatchReplay?.(deps.replayKey?.() ?? ''),
      // unmount BEFORE the action runs (so the LoadGame picker / gameOver transition isn't behind
      // the summary). On a kidcLoadGame null pick the shared handler re-calls
      // env.domObjectivesSummary → re-shows the overlay.
      onChoice: (idc: number) => {
        unmount();
        onChoice(idc);
      },
      titleBg,
      buttonArt: objBtnArt,
      playSfx: (sfx: number) => {
        playSfx(sfx);
      },
    });
  };

  // MP: the server announced the recording — flip the LIVE summary's VIEW REPLAY in place (no-op
  // when the summary isn't up, e.g. the player already left it).
  const setReplayReady = (): void => {
    view?.setReplayReady();
  };

  return { domObjectivesSummary, setReplayReady, teardown: unmount };
}
